import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/services/language/language.service';
import { GalleryService } from 'src/app/services/gallery/gallery.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpinnerComponent } from "../../layout/spinner/spinner.component";
import { UploadComponent } from "../../layout/file-manager/upload/upload.component";

type Lang = 'geo' | 'eng';

type GalleryPageOption = {
  menu_page: number;
  title?: string | null;
  slug?: string | null;
};

type GalleryItem = {
  id: number;
  lang: string;
  menu_page: number;
  type: 'image' | 'video';
  src?: string | null;
  video_url?: string | null;
  poster_src?: string | null;
  caption?: string | null;
  visible: number;
  sort: number;
};

@Component({
  selector: 'app-gallery',
  imports: [CommonModule, FormsModule, DragDropModule, SpinnerComponent, UploadComponent],
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.css'],
})
export class GalleryComponent implements OnInit, OnDestroy {
  lang: Lang = 'geo';
  private sub?: Subscription;

  loadingPages = false;
  loadingItems = false;
  saving = false;
  sorting = false;

  pages: GalleryPageOption[] = [];
  selectedMenuPage: number | null = null;

  items: GalleryItem[] = [];

  // upload folder: res/gallery/YYYY/MM
  uploadPath = '';

  togglingId: number | null = null;
  deletingIds = new Set<number>();

  constructor(
    private langService: LanguageService,
    private galleryService: GalleryService
  ) {}

  ngOnInit(): void {
    this.uploadPath = this.buildUploadPath('res/gallery');

    this.sub = this.langService.currentLanguage$.subscribe((l: any) => {
      this.lang = (l === 'eng' ? 'eng' : 'geo');
      this.loadPages();
      // items refresh for selected page
      if (this.selectedMenuPage) this.loadItems(this.selectedMenuPage);
    });

    // initial load
    this.loadPages();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private buildUploadPath(base: string): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${base}/${y}/${m}`;
  }

  getFullUrl(path?: string | null) {
    if (!path) return '';
    // თუ უკვე url არის
    if (/^https?:\/\//i.test(path)) return path;
    return `https://api.sca.ge/storage/${path}`;
  }

  loadPages() {
    this.loadingPages = true;
    this.galleryService.pages(this.lang).subscribe({
      next: (res) => {
        this.pages = res?.data ?? [];
        this.loadingPages = false;

        // თუ selectedMenuPage აღარ არსებობს, გავანულოთ
        if (this.selectedMenuPage && !this.pages.some(p => p.menu_page === this.selectedMenuPage)) {
          this.selectedMenuPage = null;
          this.items = [];
        }
      },
      error: () => {
        this.loadingPages = false;
      }
    });
  }

  onPageChange(menuPage: any) {
    const n = Number(menuPage || 0);
    this.selectedMenuPage = n > 0 ? n : null;
    this.items = [];
    if (this.selectedMenuPage) this.loadItems(this.selectedMenuPage);
  }

  loadItems(menuPage: number) {
    this.loadingItems = true;
    this.galleryService.items(menuPage, this.lang).subscribe({
      next: (res) => {
        this.items = res?.data ?? [];
        this.loadingItems = false;
      },
      error: () => {
        this.loadingItems = false;
      }
    });
  }

  // ✅ upload კომპონენტიდან მოვა res ობიექტი
  onUploaded(res: any) {
    if (!this.selectedMenuPage) {
      alert('ჯერ აირჩიე გალერეის გვერდი (menu_page).');
      return;
    }

    const path =
      res?.new?.path ??
      res?.old?.path ??
      res?.path ??
      res?.src ??
      null;

    if (!path) {
      alert('ატვირთვიდან path ვერ ამოვიღე.');
      return;
    }

    const clean = String(path).replace(/^https?:\/\/api\.sca\.ge\/storage\//, '');

    const payload = {
      menu_page: this.selectedMenuPage,
      type: 'image',
      src: clean,
      visible: 1,
      caption: null as string | null,
    };

    this.saving = true;
    this.galleryService.storeItem(payload, this.lang).subscribe({
      next: () => {
        this.saving = false;
        this.loadItems(this.selectedMenuPage!);
      },
      error: (e) => {
        this.saving = false;
        alert(e?.error?.message ?? 'შენახვაზე შეცდომა');
      }
    });
  }

  toggleVisible(item: GalleryItem) {
    this.togglingId = item.id;
    this.galleryService.toggleVisible(item.id, this.lang).subscribe({
      next: (res: any) => {
        item.visible = res?.visible ?? item.visible;
        this.togglingId = null;
      },
      error: () => {
        this.togglingId = null;
      }
    });
  }

  delete(item: GalleryItem) {
    if (!confirm('წავშალო?')) return;

    this.deletingIds.add(item.id);
    this.galleryService.delete(item.id, this.lang, false).subscribe({
      next: () => {
        this.deletingIds.delete(item.id);
        if (this.selectedMenuPage) this.loadItems(this.selectedMenuPage);
      },
      error: () => {
        this.deletingIds.delete(item.id);
      }
    });
  }

  drop(event: CdkDragDrop<GalleryItem[]>) {
    if (!this.selectedMenuPage) return;

    moveItemInArray(this.items, event.previousIndex, event.currentIndex);

    const ids = this.items.map(x => x.id);
    this.sorting = true;
    this.galleryService.sort(this.selectedMenuPage, ids, this.lang).subscribe({
      next: () => (this.sorting = false),
      error: () => (this.sorting = false),
    });
  }
}